/**
 * 특정 워크스페이스에서만 노출되는 기능 게이트.
 *
 * 언어(tL) 필터(사이드 필터 + 캠페인 전용필터) 노출과 포스팅 리스트 엑셀의
 * 언어·페르소나 컬럼은 LANGUAGE_PERSONA_FEATURE_WORKSPACE_IDS 에 속한
 * 워크스페이스에서만 활성화된다.
 * 타겟 키워드 검색량 트렌드 메뉴는 별도로 단일 워크스페이스에만 노출된다.
 * 캠페인 포스트 표의 저장·공유·다운로드 열은 CAMPAIGN_HIDDEN_POST_METRICS_BY_WORKSPACE 에
 * 적힌 워크스페이스에서만 숨긴다(값·E/R 계산은 그대로 — 열만 안 그린다).
 * `MOCKUP_` 워크스페이스는 그 표 대신 registerMockupHiddenPostMetrics 가 심은 집합을 본다.
 * 캠페인 목록의 첫 보기는 기본 「전체 캠페인」(평면)이고, CAMPAIGN_LIST_TREE_DEFAULT_WORKSPACE_IDS 의
 * 워크스페이스만 「그룹 보기」(드라이브 루트)로 연다.
 */
import { isMockupId } from 'src/mockup/mockup-id';

const LANGUAGE_PERSONA_FEATURE_WORKSPACE_ID = 'MMmwFyhW3GTeOY3Ij0oXc9k5fXv1_39H2GPDT4U';

const LANGUAGE_PERSONA_FEATURE_WORKSPACE_IDS = new Set([
  LANGUAGE_PERSONA_FEATURE_WORKSPACE_ID,
  'uxquc12RemgFXjMZViIcjd3334S2_WCEPMLVQB7',
]);

const SEARCH_TREND_FEATURE_WORKSPACE_ID = LANGUAGE_PERSONA_FEATURE_WORKSPACE_ID;

/** 포스트 표의 반응 지표 열 이름 — PostTable 의 지표 열 키와 같다 */
export type PostMetricField = 'like' | 'comment' | 'saved' | 'share' | 'download';

const NO_HIDDEN_POST_METRICS: ReadonlySet<PostMetricField> = new Set();

/**
 * mockup 라우트가 심는 숨김 열 — **`MOCKUP_` 워크스페이스에만** 걸린다.
 *
 * 영업 메일 캡처가 표 폭을 줄이려고 주입으로 정한다(`data.hiddenMetrics` →
 * `src/mockup/campaign/campaign-mockup-backend`). 실 워크스페이스는 숨기지 않으므로
 * 등록돼 있어도 운영 화면은 한 칸도 달라지지 않는다(실 화면은 「열 설정」에서 사람이 끈다).
 */
let mockupHiddenPostMetrics: ReadonlySet<PostMetricField> = NO_HIDDEN_POST_METRICS;

/**
 * mockup 숨김 열을 심거나(배열) 지운다(`null`).
 * @param fields 숨길 열 — `null`·빈 배열이면 숨기지 않는다
 */
export function registerMockupHiddenPostMetrics(fields: Iterable<PostMetricField> | null | undefined): void {
  const set = fields ? new Set(fields) : null;
  mockupHiddenPostMetrics = set && set.size > 0 ? set : NO_HIDDEN_POST_METRICS;
}

/**
 * @param {string} workspaceId
 * @returns {boolean}
 */
export function isLanguagePersonaFeatureWorkspace(workspaceId: unknown): boolean {
  return LANGUAGE_PERSONA_FEATURE_WORKSPACE_IDS.has(String(workspaceId ?? '').trim());
}

/**
 * 타겟 키워드 검색량 트렌드 메뉴/페이지 노출 여부.
 * @param {string} workspaceId
 * @returns {boolean}
 */
export function isSearchTrendFeatureWorkspace(workspaceId: unknown): boolean {
  return String(workspaceId ?? '').trim() === SEARCH_TREND_FEATURE_WORKSPACE_ID;
}

/**
 * 캠페인 화면의 포스트 표에서 숨길 반응 지표 열 — mockup 워크스페이스만 숨긴다.
 * @param {string} workspaceId
 * @returns {ReadonlySet<PostMetricField>} 숨길 열의 집합 — 해당 없으면 빈 집합(같은 인스턴스)
 */
export function getCampaignHiddenPostMetrics(workspaceId: unknown): ReadonlySet<PostMetricField> {
  return isMockupId(String(workspaceId ?? '').trim()) ? mockupHiddenPostMetrics : NO_HIDDEN_POST_METRICS;
}
